import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import { omit } from 'lodash';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import rubricService from 'services/rubric';
import userService from 'services/user';

const AppContext = createContext();
const useApp = () => useContext(AppContext);

const mergeAccountsAndUsers = (accounts, users) => {
    return accounts
        .filter(({ active }) => active)
        .map((account) => {
            const user = users.find(({ id }) => id === account.user_id);
            const userAccounts = accounts.filter(({ user_id }) => user_id === user.id);
            const isPending = userAccounts.some(({ login_activity }) => Boolean(login_activity));
            return {
                ...omit(account, ['_id', 'id']),
                ...omit(user, ['_id', 'id']),
                owner_account_id: account.id,
                user_id: user.id,
                is_pending: isPending,
            };
        });
};

const AppProvider = (props) => {
    const { auth, logout } = useAuth();
    const location = useLocation();
    const { batchWS } = useWebsocket();

    const isCancelled = useRef(false);

    // default value null means uninitialized unless stated otherwise
    const [course, setCourse] = useState(null);
    const [accountUsers, setAccountUsers] = useState(null); // account document and user document merged
    const [assignments, setAssignments] = useState(null);
    const [currentAssignmentId, setCurrentAssignmentId] = useState(null); // the assignment you are currently looking at
    const [batch, setBatch] = useState(null); // ongoing backend jobs for current assignment
    const [feedback, setFeedback] = useState(null); // feedback for current assignment and current accountUser
    const [submissions, setSubmissions] = useState(null); // submissions for current assignment
    const [rubrics, setRubrics] = useState(null);
    const [customer, setCustomer] = useState(); // stripe customer, undefined means uninitialized, null means no customer
    const [subscription, setSubscription] = useState(); // stripe subscription, undefined means uninitialized, null means no subscription
    const [entitlements, setEntitlements] = useState(null); // stripe entitlements

    const isLoading = !course || !accountUsers || !assignments || typeof customer === 'undefined' || !entitlements;
    const instructorAccountUsers = useMemo(
        () => accountUsers?.filter(({ role }) => role === 'instructor'),
        [accountUsers]
    );
    const studentAccountUsers = accountUsers?.filter(({ role }) => role === 'student');
    const currentAssignment = assignments?.find(({ id }) => id === currentAssignmentId);

    const updateCourse = async (courseData) => {
        try {
            await courseService.updateCourse(auth.course_id, courseData);
            return setCourse((prev) => ({ ...prev, ...courseData }));
        } catch (e) {
            console.error(e);
        }
    };

    const addAccountUser = (accountUser) => {
        setAccountUsers((current) => [...current, accountUser]);
    };

    const removeAccountUser = (accountUser) => {
        setAccountUsers((current) =>
            current.filter(({ owner_account_id }) => owner_account_id !== accountUser.owner_account_id)
        );
    };

    useEffect(() => {
        if (!auth?.course_id) return;
        (async () => {
            const course = await courseService.findCourse(auth.course_id);
            setCourse(course);
        })();
    }, [auth?.course_id]);

    useEffect(() => {
        if (!course) return;
        (async () => {
            const [accounts, users, assignments] = await Promise.all([
                await courseService.listCourseAccounts(course.id),
                await courseService.listCourseUsers(course.id),
                await courseService.listCourseAssignments(course.id),
            ]);
            setAccountUsers(mergeAccountsAndUsers(accounts, users));
            setAssignments(assignments);
        })();
    }, [course]);

    useEffect(() => {
        if (location.pathname.startsWith('/assignments')) {
            const assignmentId = location.pathname.split('/')[2];
            if (!assignmentId) {
                setCurrentAssignmentId(null);
            }
            setCurrentAssignmentId(assignmentId);
        } else {
            setCurrentAssignmentId(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        setBatch(null);
        setFeedback(null);
        setSubmissions(null);

        if (!auth) return;

        if (!currentAssignmentId || !instructorAccountUsers) {
            isCancelled.current = true;
            return;
        } else {
            isCancelled.current = false;
        }

        (async () => {
            const instructorOwnerAccountIds = instructorAccountUsers.map(({ owner_account_id }) => owner_account_id);
            const [batch, feedback, submissions] = await Promise.all([
                await assignmentService.getAssignmentBatch(
                    currentAssignmentId,
                    JSON.stringify({
                        is_finished: false,
                        ...(auth.role === 'student'
                            ? { owner_account_id: auth.id }
                            : {
                                  owner_account_id: instructorOwnerAccountIds,
                              }),
                    })
                ),
                await assignmentService.getAssignmentFeedback(
                    currentAssignmentId,
                    auth.role === 'student' ? JSON.stringify({ owner_account_id: auth.id }) : undefined
                ),
                await assignmentService.getAssignmentSubmissions(currentAssignmentId),
            ]);
            if (isCancelled.current) return;
            setBatch(batch);
            setFeedback(feedback);
            setSubmissions(submissions);
        })();
    }, [currentAssignmentId, auth, instructorAccountUsers]);

    useEffect(() => {
        if (!auth) return;
        if (batchWS && batch?.length) {
            batch.forEach(({ id }) => {
                const updated = batchWS.find((b) => b.id === id);
                if (updated) {
                    (async () => {
                        const submissions = await assignmentService.getAssignmentSubmissions(currentAssignmentId);
                        setSubmissions(submissions);
                        if (auth.role === 'student') {
                            const feedback = await assignmentService.getAssignmentFeedback(
                                currentAssignmentId,
                                JSON.stringify({ owner_account_id: auth.id })
                            );
                            setFeedback(feedback);
                        }
                        if (updated?.is_finished) {
                            setBatch((current) => current.filter((item) => item.id !== id));
                        }
                    })();
                }
            });
        }
    }, [auth, batch, batchWS, currentAssignmentId]);

    useEffect(() => {
        if (!auth?.user_id) return;
        (async () => {
            const rubrics = await rubricService.listRubrics(
                JSON.stringify({
                    user_id: auth.user_id,
                })
            );
            setRubrics(rubrics);
        })();
    }, [auth?.user_id]);

    useEffect(() => {
        if (!auth?.user_id) return;
        (async () => {
            if (typeof customer === 'undefined') {
                const customers = await userService.getUserCustomer(auth.user_id);
                if (customers.length === 0) {
                    await logout({ isExpired: true });
                } else if (customers.length === 1) {
                    setCustomer(customers[0]);
                }
            }
        })();
    }, [auth?.user_id, customer, logout]);

    useEffect(() => {
        if (!auth?.user_id) return;

        if (!customer) {
            setSubscription(null);
            setEntitlements([]);
            return;
        }

        (async () => {
            const subscription = await userService.getUserSubscriptions(auth.user_id, customer.id);
            setSubscription(subscription);
        })();
        (async () => {
            const entitlements = await userService.getUserEntitlements(auth.user_id, customer.id);
            setEntitlements(entitlements);
        })();
    }, [auth?.user_id, customer]);

    return (
        <AppContext.Provider
            {...props}
            value={{
                isLoading,

                // core data
                course,
                accountUsers,
                setAccountUsers,
                instructorAccountUsers, // accountUsers with role 'instructor'
                studentAccountUsers, // accountUsers with role 'student'
                assignments,
                setAssignments,

                // assignment related data
                currentAssignmentId,
                setCurrentAssignmentId,
                currentAssignment,
                batch,
                setBatch,
                feedback,
                setFeedback,
                submissions,
                setSubmissions,
                rubrics,
                setRubrics,

                // stripe
                customer,
                subscription,
                setSubscription,
                entitlements,
                setEntitlements,

                updateCourse,
                addAccountUser,
                removeAccountUser,
            }}
        />
    );
};

export { useApp };
export default AppProvider;
